import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../../pixiSettings";
import anime from "animejs/lib/anime.es.js";
import AdditionalFreeSpinsController from "./AdditionalFreeSpinsController";
import { safeInit } from "../../../../utilities";
import { selectShowAdditionalFreeSpins } from "../../../../State/Slices/animationsSlice";

/**
 * AdditionalFreeSpinsDialog — "you won more free spins", shown mid-feature.
 *
 * Was FreeSpinRetriggerDialog, which played one clip and closed itself off a
 * timer. It now waits for the player like every other popup, so it is built on
 * the same shape as FreeSpinIntroDialog:
 *
 *   - The component NEVER unmounts. Visibility is alpha, which is what gives
 *     the spine's `out` a chance to play before the flag clears.
 *   - The overlay fades through `overlayRef`, driven by the controller.
 *   - Closing is the controller's job: `out`, then the flag, then the fade.
 */

const OVERLAY_BACKGROUND_COLOR = POPUP_OVERLAY_CONFIG.COLOR;
const OVERLAY_BACKGROUND_ALPHA = POPUP_OVERLAY_CONFIG.ALPHA;
const OVERLAY_FADE_DURATION = POPUP_OVERLAY_CONFIG.FADE_DURATION;

const AdditionalFreeSpinsDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const show = useSelector(selectShowAdditionalFreeSpins);
  const { additionalFreeSpinsControllerRef } = useReelRefs();

  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, uiSymbolHeight: uiSymbolSize, device } = dimensionsContext;

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // ── What actually makes this popup modal ──
  //
  // Raised by the CONTROLLER, when it fades the dim in, and lowered when that
  // dim has gone. So it is true exactly while there is something on screen to
  // be modal about — which `show` is not.
  //
  // `show` is a Redux flag the CALLER raises one line before startSequence, and
  // every path where the popup declines to open used to leave it up. Gating the
  // absorber and the key swallow on it meant a refused popup locked the game
  // behind a transparent rectangle: the "+7 got stuck" from demo 2. The
  // controller now clears the flag on those paths too, and this is the second
  // lock on the same door — an absorber cannot outlive the dim it belongs to,
  // whatever the flag says.
  const [armed, setArmed] = useState(false);
  const modal = show && armed;

  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      // Before the tween, not inside its first update: anime's opening frame
      // can still report 0, and a frame of open door is a frame too many.
      setArmed(true);
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => {
          if (typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => {
          setOverlayAlpha(0);
          setArmed(false);
          if (typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
      // A dim set directly is still a dim. Follows it either way, so this
      // cannot become a third path that leaves the door open or shut wrongly.
      setArmed(alpha > 0);
    },
  }));

  // Initialize controller once — it lives for the session.
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new AdditionalFreeSpinsController(
          containerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          t,
        );

        // Published HERE, inside the deferred construction, not from an effect
        // of its own. safeInit waits a frame, so an effect that publishes on
        // mount reads null — and with nothing in its deps that ever changes
        // again, it stays null for the session. The level-up sequence then
        // found no popup, skipped it, and committed the room colours on the
        // spot: the swap with no popup in front of it.
        if (additionalFreeSpinsControllerRef) {
          additionalFreeSpinsControllerRef.current = controllerRef.current;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    controllerRef.current?.updateDimensions(dimensionsContext, uiSymbolSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Deliberately NOT started from `show`.
  //
  // The level-up sequence calls startSequence itself, with the hooks it needs
  // — it has to know when the popup reaches idle (to change the room behind
  // it) and when it has closed (to play the box's conversion). A self-start
  // here would race that and run the popup with no hooks attached.
  useEffect(() => {
    return () => {
      if (additionalFreeSpinsControllerRef?.current === controllerRef.current) {
        additionalFreeSpinsControllerRef.current = null;
      }
    };
  }, [additionalFreeSpinsControllerRef]);

  // ── Nothing here takes input ──
  //
  // No tap word, no background click, no spacebar. This popup plays ONE clip
  // and closes on its last frame — a player press is not one of the ways it can
  // end, so the handlers that offered one are gone rather than left dark. The
  // hold it used to take after its entry clip is gone with them; see the
  // controller.

  // ── ...and nothing UNDER it takes input either ──
  //
  // Taking no input and BLOCKING input are two different jobs, and this popup
  // only ever did the first. The dim below now absorbs the pointer, but a
  // keydown listener on `document` is not stopped by anything in the PIXI
  // tree, so the spacebar still reached FreeSpinBasicControlContext's handler
  // and the reveal-skip's window listener — the game was playable under a
  // popup that cannot be skipped.
  //
  // So: swallow it. CAPTURE phase on `document`, which runs before every
  // bubble-phase listener in the game, and `stopPropagation` there ends the
  // event's journey entirely — it never reaches the target, never bubbles
  // back, and never reaches `window`. That is the same mechanism the Total
  // Win, Max Win and Zero Win dialogs already use; the only difference is that
  // they call an action afterwards and this one deliberately calls nothing.
  //
  // `stopImmediatePropagation` as well, for the one case plain
  // `stopPropagation` does not cover: another CAPTURE listener on `document`
  // itself, which would still run. The intro and outro dialogs both carry it.
  useEffect(() => {
    if (!modal) return undefined;

    const swallow = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }
      // Space scrolls the page when the canvas does not hold focus, so this is
      // needed even though nothing acts on the key.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    document.addEventListener("keydown", swallow, {
      capture: true,
      passive: false,
    });
    return () =>
      document.removeEventListener("keydown", swallow, { capture: true });
  }, [modal]);

  useEffect(() => {
    return () => {
      controllerRef.current?.cleanup();
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  const containerPosition = useMemo(
    () => ({ x: dimensions.width / 2, y: dimensions.height / 2 }),
    [dimensions.width, dimensions.height],
  );

  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="additional_free_spins"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={9}
      ref={containerRef}
      // Gates the WHOLE subtree. The tap-to-continue container is eventMode
      // "static" with its own hit area, and closing only drops the spine's
      // alpha — so without this it stays invisible but still swallowing
      // pointer events, for every popup, for ever. Every Zeus popup carries
      // this guard; these five were missing it.
      eventMode={modal ? "auto" : "none"}
    >
      {/* Drawn back to 0,0 so the rect covers the screen while the spine
          stays centred. */}
      <Graphics
        name="additional_free_spins_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        // ── IT SWALLOWS INPUT, it does not pass it on ──
        //
        // This was `"none"`, on the reasoning that the popup accepts no input.
        // Those are two different things: `"none"` makes the dim invisible to
        // the pointer, so a tap went STRAIGHT THROUGH it to the toolbar
        // underneath. The popup is a critical animation like every other one
        // here, so it is modal — the rect covers the screen, absorbs the press
        // and drops it.
        //
        // Nothing stopped a press before except FreeSpinButtonPlay happening to
        // be non-interactive at that moment, and its `isInteractive` does not
        // read `criticalAnimationPlaying` — the one flag this popup raises. So
        // the protection lived in another component, keyed on flags this popup
        // does not control. A retrigger whose win presentation overlapped the
        // announcement would have left the play button live under the dim.
        //
        // `static` with no handler is the whole implementation: it is hit-
        // testable, so it consumes the event, and it does nothing with it.
        // Hit area follows `draw` automatically for a filled Graphics.
        //
        // Gated on `show` as well as the parent, because the overlay outlives
        // the spine's alpha by a fade — a screen-sized absorber left live after
        // the popup has gone is exactly the fault the container guard above was
        // added for.
        eventMode={modal ? "static" : "none"}
      />
      {/* Spine, label and tap-to-continue are owned by the controller and
          pinned into the spine's own slots. */}
    </Container>
  );
};

export default memo(AdditionalFreeSpinsDialog);
